<template lang="html">
  <nav
    class="hidden items-center gap-0.5 lg:flex"
    aria-label="Быстрые действия"
  >
    <NuxtLink
      to="/profile/favorites"
      class="flex min-w-14 flex-col items-center gap-0.5 rounded-md px-2 py-1 text-[10px] leading-none text-muted no-underline transition-colors hover:bg-elevated hover:text-highlighted focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none motion-reduce:transition-none"
    >
      <UIcon
        name="i-lucide-heart"
        class="size-5 text-tertiary-500"
      />
      <span>Избранное</span>
    </NuxtLink>

    <NuxtLink
      :to="cartRouting.index.route"
      class="flex min-w-14 flex-col items-center gap-0.5 rounded-md px-2 py-1 text-[10px] leading-none text-muted no-underline transition-colors hover:bg-elevated hover:text-highlighted focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none motion-reduce:transition-none"
      active-class="bg-elevated text-highlighted"
    >
      <UChip
        :show="cartCount > 0"
        :text="cartCountLabel"
        color="primary"
        size="3xl"
        :ui="{ base: 'h-4 min-w-4 px-1 text-[11px] leading-none' }"
      >
        <UIcon
          name="i-lucide-shopping-cart"
          class="size-5 text-tertiary-500"
        />
      </UChip>
      <span>Корзина</span>
    </NuxtLink>

    <UButton
      v-if="!isFullAuth"
      variant="link"
      color="tertiary"
      class="h-auto! min-w-14 cursor-pointer flex-col gap-0.5 px-2 py-1! focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none motion-reduce:transition-none"
      @click="openAuth()"
    >
      <UIcon
        name="i-lucide-user"
        class="size-5 text-tertiary-500"
      />
      <span class="text-[10px] leading-none font-normal text-muted">
        Войти
      </span>
    </UButton>

    <UDropdownMenu
      v-else
      :items="userMenu"
    >
      <UChip
        :show="notificationsStore.hasUnread"
        color="primary"
        size="sm"
        inset
      >
        <UButton
          variant="link"
          color="tertiary"
          class="h-auto! min-w-14 cursor-pointer flex-col gap-0.5 px-2 py-1! focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none motion-reduce:transition-none"
          aria-label="Профиль"
        >
          <UIcon
            name="i-lucide-user"
            class="size-5 text-tertiary-500"
          />
          <span class="text-[10px] leading-none font-normal text-muted">
            Профиль
          </span>
        </UButton>
      </UChip>

      <template #notifications-trailing>
        <div
          v-if="notificationsStore.hasUnread"
          class="flex items-center gap-1.5"
        >
          <UBadge
            color="primary"
            variant="subtle"
            size="sm"
          >
            {{ notificationsStore.unreadCount }}
          </UBadge>
          <div class="h-1.5 w-1.5 rounded-full bg-primary" />
        </div>
      </template>
    </UDropdownMenu>
  </nav>
</template>

<script lang="ts" setup>
import type { DropdownMenuItem } from '@nuxt/ui'

import { useNotifications } from '~/app/AltApplication/Modules/Notifications/composables/useNotifications'

const { openAuth } = useAuthInteractive()
const { store, logout } = useAuth()
const { store: userStore } = useUser()
const { store: cartStore, routing: cartRouting } = useCartModule()
const { store: notificationsStore, routing: notificationsRouting }
  = useNotifications()
const { send } = useRequest()

const isFullAuth = computed(() => !!store.token && !store.session?.isAnon)

const cartCount = computed(() => cartStore.totalLength || 0)
const cartCountLabel = computed(() =>
  cartCount.value > 99 ? '99+' : String(cartCount.value),
)

const userMenu = computed<DropdownMenuItem[][]>(() => [
  [
    {
      label: userStore.user?.email || userStore.user?.phone || 'Пользователь',
      to: '/profile/edit',
      class: 'font-bold',
    },
  ],
  [
    {
      label: 'Уведомления',
      icon: 'i-lucide-bell',
      to: notificationsRouting.index,
      slot: 'notifications',
    },
    {
      label: 'Мои заказы',
      icon: 'i-lucide-package',
      to: '/profile/orders',
    },
    {
      label: 'Мои бонусы',
      icon: 'i-lucide-wallet',
      to: '/profile/loyalty',
    },
    {
      label: 'Избранное',
      icon: 'i-lucide-heart',
      to: '/profile/favorites',
    },
  ],
  [
    {
      label: 'Выйти',
      icon: 'i-lucide-log-out',
      onSelect: () => {
        send(
          async () => {
            await logout()
            navigateTo('/')
          },
          {
            successMessage: 'Вы вышли из системы',
            confirm: {
              message: 'Вы уверены, что хотите выйти?',
              accept: 'Выйти',
              reject: 'Отмена',
            },
          },
        )
      },
    },
  ],
])
</script>
